/**
 * What a failed embed read means to its reader.
 *
 * The `visitor` surface answers a refusal with base's error body, whose `message` is an English
 * sentence for a developer. A reader is shown none of it: the status alone says which screen to
 * draw, in the browser's language (`PlayerRefusal.vue`). The embed page answers a visitor's
 * refusals before the player exists, in its own template, with the same French words; the
 * member's kinds (`missing`, `denied`, `signedOut`) have no page there, since no member opens one.
 */

import { ApiError } from '@/api/client'

export type EmbedFailure =
  | 'unavailable'
  | 'restricted'
  | 'origin'
  | 'throttled'
  | 'outage'
  | 'missing'
  | 'denied'
  | 'signedOut'

/**
 * Who reads the failure. A `visitor` opened a publication somebody handed out; a `member` opens
 * an asset in the application, where a `404` is a medium gone, a `403` a permission and a `401`
 * a session that ended — never a link that expired or a site its owner did not allow.
 */
export type EmbedAudience = 'visitor' | 'member'

/**
 * Classify what `readEmbed` threw.
 *
 * Every row refusal is one `404`, so `unavailable` says nothing about which it was. A failure
 * that is not an answer at all — the network, a body that would not parse — is an outage, the
 * one kind worth retrying.
 */
export function embedFailureOf(error: unknown, audience: EmbedAudience = 'visitor'): EmbedFailure {
  if (!(error instanceof ApiError)) return 'outage'
  if (audience === 'member') {
    if (error.status === 404) return 'missing'
    if (error.status === 403) return 'denied'
    if (error.status === 401) return 'signedOut'
  }
  switch (error.status) {
    case 404:
      return 'unavailable'
    case 401:
      return 'restricted'
    case 403:
      return 'origin'
    case 429:
      return 'throttled'
    default:
      return 'outage'
  }
}

/** Whether trying again can change the answer: a refusal stays a refusal until its owner acts. */
export function isRetryable(failure: EmbedFailure): boolean {
  return failure === 'outage' || failure === 'throttled'
}
